import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const STEPS = [
  { n: "01", title: "Discovery", kr: "요구사항 분석", desc: "비즈니스 목표와 사용자 시나리오를 명확히 합니다." },
  { n: "02", title: "Strategy", kr: "서비스 구조 설계", desc: "데이터·기능·운영의 전체 구조를 설계합니다." },
  { n: "03", title: "Design", kr: "UI·UX 디자인", desc: "브랜드 톤과 사용성의 균형을 디자인합니다." },
  { n: "04", title: "Development", kr: "프론트·백엔드 개발", desc: "현대적인 스택으로 빠르고 안정적인 시스템을 구축합니다." },
  { n: "05", title: "AI Integration", kr: "AI 기능 연동", desc: "LLM·자동화 워크플로우를 핵심 흐름에 통합합니다." },
  { n: "06", title: "Launch", kr: "배포 및 운영", desc: "지속 가능한 운영과 점진적 개선을 시작합니다." },
];

export default function ProcessSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const progress = useTransform(scrollYProgress, [0.15, 0.85], ["0%", "100%"]);

  const [active, setActive] = useState(0);
  useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      const idx = Math.min(STEPS.length - 1, Math.max(0, Math.floor((v - 0.15) / 0.7 * STEPS.length)));
      setActive(idx);
    });
  }, [scrollYProgress]);

  return (
    <section
      id="process"
      data-testid="process-section"
      ref={ref}
      className="relative bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#0A2540]">
            Process — 04
          </div>
          <h2 className="font-display font-kr mt-5 text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
            아이디어가 운영되는<br />플랫폼이 되기까지.
          </h2>
        </motion.div>

        {/* Timeline progress bar */}
        <div className="relative mt-14 md:mt-20">
          <div className="absolute left-0 right-0 top-6 h-px bg-slate-200" aria-hidden="true" />
          <motion.div
            className="absolute left-0 top-6 h-px bg-[#0A2540]"
            style={{ width: progress }}
            aria-hidden="true"
          />

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-12 gap-x-6 relative">
            {STEPS.map((s, i) => {
              const isActive = i <= active;
              return (
                <div
                  key={s.n}
                  data-testid={`process-step-${s.n}`}
                  className="relative pt-12"
                >
                  <div
                    className={`absolute top-3.5 left-0 h-5 w-5 rounded-full border transition-all duration-500 ${
                      isActive
                        ? "bg-[#0A2540] border-[#0A2540] scale-100"
                        : "bg-white border-slate-300 scale-90"
                    }`}
                  />
                  <div className={`font-display text-xs font-semibold tracking-[0.2em] ${isActive ? "text-[#0A2540]" : "text-slate-400"}`}>
                    {s.n}
                  </div>
                  <div className="font-display mt-2 text-lg md:text-xl font-bold tracking-tight text-slate-900">
                    {s.title}
                  </div>
                  <div className="font-kr mt-1 text-sm font-medium text-slate-500">{s.kr}</div>
                  <p className="font-kr mt-3 text-[13px] text-slate-500 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
